/**
 * Sample CTA Web Component
 * Adds the linked sample product to the cart via AJAX Cart API.
 * Automatically matches the parent product's selected variant.
 *
 * Rules:
 * - Max 8 samples total in cart (no minimum)
 * - 1 per click, no duplicates (same variant blocked)
 * - Variant auto-matched from parent product selection (closest match
 *   fallback if the exact combo doesn't exist as a sample)
 */
class SampleCta extends HTMLElement {
  static MAX_SAMPLES = 8;

  constructor() {
    super();
    this.variantId = null;
    this.button = null;
    this.variants = [];
    this.infoText = null;
    this.baseInfoText = '';
  }

  connectedCallback() {
    this.variantId = this.dataset.sampleVariantId;
    this.button = this.querySelector('[data-ref="sample-btn"]');
    this.btnText = this.querySelector('[data-ref="btn-text"]');
    this.infoText = this.querySelector('[data-ref="info-text"]');
    this.baseInfoText = this.infoText?.textContent?.trim() || '';
    this.baseBtnText = this.btnText?.textContent?.trim() || '';

    // Parse sample variants data
    try {
      this.variants = JSON.parse(this.dataset.sampleVariants || '[]');
    } catch (e) {
      this.variants = [];
    }

    if (!this.variantId || !this.button) return;

    // Update counter on load
    this.updateSampleCounter();

    // Listen to add-to-cart button click
    this.button.addEventListener('click', (e) => {
      e.preventDefault();
      this.matchParentVariant();
      this.addSampleToCart();
    });

    // Listen for cart updates to refresh counter
    document.addEventListener('cart:updated', () => {
      setTimeout(() => this.updateSampleCounter(), 500);
    });
  }

  /**
   * Read currently selected option values from the parent (wallpaper) variant
   * picker and pick the best sample variant :
   *   1. Exact match on all options → use it.
   *   2. No exact match → pick the variant with the most matching options
   *      (colour first, then method, etc.). Flag as "closest match" so the
   *      caller can tell the user.
   */
  matchParentVariant() {
    if (this.variants.length === 0) return;

    // Scope the lookup to the current product's section, not the whole page,
    // so we don't accidentally pick a variant-picker from a quick-add popup
    // or another block on the page.
    const scope =
      this.closest('[data-ref="product-section-content"]') ||
      this.closest('.shopify-section') ||
      document;
    const variantPicker = scope.querySelector('variant-picker') || document.querySelector('variant-picker');

    // Read values from the inputs directly (option value, not label text)
    const selectedValues = variantPicker
      ? Array.from(variantPicker.querySelectorAll('fieldset input:checked')).map((i) => i.value)
      : [];

    this.isClosestMatch = false;
    this.requestedSelection = selectedValues.slice();

    let matchingVariant = null;

    if (selectedValues.length > 0) {
      // 1. exact match
      matchingVariant = this.variants.find((sv) => {
        const opts = [sv.option1, sv.option2, sv.option3].filter(Boolean);
        return opts.length === selectedValues.length && opts.every((o) => selectedValues.includes(o));
      });

      // 2. closest match (max shared options) among AVAILABLE variants
      if (!matchingVariant) {
        let best = null;
        let bestScore = -1;
        for (const sv of this.variants) {
          if (sv.available === false) continue;
          const opts = [sv.option1, sv.option2, sv.option3].filter(Boolean);
          const score = opts.filter((o) => selectedValues.includes(o)).length;
          if (score > bestScore) {
            bestScore = score;
            best = sv;
          }
        }
        if (best) {
          matchingVariant = best;
          this.isClosestMatch = true;
        }
      }
    }

    // 3. Fallback to the first variant if nothing else works
    if (!matchingVariant) {
      matchingVariant = this.variants[0];
    }

    if (matchingVariant) {
      this.variantId = matchingVariant.id.toString();
      this.matchedVariant = matchingVariant;

      if (this.infoText && this.baseInfoText) {
        const variantLabel = [matchingVariant.option1, matchingVariant.option2, matchingVariant.option3]
          .filter(Boolean)
          .join(' / ');
        this.infoText.textContent = this.baseInfoText + ' • ' + variantLabel;
      }
    }
  }

  async updateSampleCounter() {
    try {
      const response = await fetch(window.Shopify?.routes?.root + 'cart.js', {
        headers: { Accept: 'application/json' },
      });
      const cart = await response.json();
      const sampleCount = cart.items
        .filter((item) => item.product_type === 'Échantillon')
        .reduce((sum, item) => sum + item.quantity, 0);

      if (this.btnText) {
        if (sampleCount > 0 && sampleCount < SampleCta.MAX_SAMPLES) {
          this.btnText.textContent = `${this.baseBtnText} (${sampleCount}/${SampleCta.MAX_SAMPLES})`;
        } else if (sampleCount >= SampleCta.MAX_SAMPLES) {
          this.btnText.textContent = `${this.baseBtnText} (${SampleCta.MAX_SAMPLES}/${SampleCta.MAX_SAMPLES}) — max atteint`;
          this.button.disabled = true;
        } else {
          this.btnText.textContent = this.baseBtnText;
        }
      }
    } catch (e) {
      // Silently fail
    }
  }

  /**
   * Show the "closest match" modal with the exact combo the user requested
   * and the one we actually added.
   */
  showMismatchDialog() {
    const dialog = this.querySelector('[data-ref="mismatch-dialog"]');
    if (!dialog) return;

    const requestedLabel = (this.requestedSelection || []).filter(Boolean).join(' / ') || '—';
    const addedLabel =
      [this.matchedVariant.option1, this.matchedVariant.option2, this.matchedVariant.option3]
        .filter(Boolean)
        .join(' / ') || '—';

    const reqEl = dialog.querySelector('[data-ref="requested-variant"]');
    const addEl = dialog.querySelector('[data-ref="added-variant"]');
    if (reqEl) reqEl.textContent = requestedLabel;
    if (addEl) addEl.textContent = addedLabel;

    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      // Fallback pour les très vieux navigateurs : toast classique
      document.dispatchEvent(
        new CustomEvent('toast:open', {
          detail: {
            type: 'info',
            message: `Combinaison indisponible — nous avons ajouté "${addedLabel}".`,
          },
        })
      );
    }
  }

  async addSampleToCart() {
    if (!this.variantId) return;

    this.button.classList.add('is-loading');
    this.button.disabled = true;

    const cartDrawer = document.querySelector('[data-ref="cart-drawer"]');
    const sectionId = cartDrawer?.dataset?.sectionId;
    const sampleVariantIds = this.variants.map((v) => v.id);

    try {
      // Fetch current cart to check limits
      const cartCheck = await fetch(window.Shopify?.routes?.root + 'cart.js', {
        headers: { Accept: 'application/json' },
      });
      const cart = await cartCheck.json();

      // Count existing samples in cart
      const samplesInCart = cart.items.filter((item) => sampleVariantIds.includes(item.variant_id));
      const totalSampleQty = samplesInCart.reduce((sum, item) => sum + item.quantity, 0);

      // Rule: block duplicate (same variant already in cart)
      const duplicateVariant = samplesInCart.find((item) => item.variant_id === parseInt(this.variantId));
      if (duplicateVariant) {
        document.dispatchEvent(
          new CustomEvent('toast:open', {
            detail: {
              type: 'error',
              message: 'Cet échantillon est déjà dans votre panier.',
            },
          })
        );
        return;
      }

      // Rule: max 8 samples total
      if (totalSampleQty >= SampleCta.MAX_SAMPLES) {
        document.dispatchEvent(
          new CustomEvent('toast:open', {
            detail: {
              type: 'error',
              message: `Vous avez atteint le maximum de ${SampleCta.MAX_SAMPLES} échantillons.`,
            },
          })
        );
        return;
      }

      const response = await fetch(window.Shopify?.routes?.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: parseInt(this.variantId),
              quantity: 1,
            },
          ],
          ...(sectionId ? { sections: sectionId } : {}),
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || "Erreur lors de l'ajout");
      }

      const responseData = await response.json();

      const newSampleCount = totalSampleQty + 1;
      const remaining = SampleCta.MAX_SAMPLES - newSampleCount;

      if (this.isClosestMatch && this.matchedVariant) {
        // Combination mismatch: show a dialog, not a toast.
        this.showMismatchDialog();
      } else {
        const toastMessage =
          remaining > 0
            ? `Échantillon ajouté ! (${newSampleCount}/${SampleCta.MAX_SAMPLES})`
            : `Échantillon ajouté ! (${SampleCta.MAX_SAMPLES}/${SampleCta.MAX_SAMPLES}) — maximum atteint`;

        document.dispatchEvent(
          new CustomEvent('toast:open', {
            detail: { type: 'success', message: toastMessage },
          })
        );
      }

      const cartIcon = document.querySelector('cart-icon');
      const currentCount = cartIcon
        ? parseInt(cartIcon.querySelector('[data-ref="cart-count"]')?.textContent || '0')
        : 0;

      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          bubbles: true,
          detail: {
            resource: {},
            data: {
              itemCount: currentCount + 1,
              source: 'sample-cta',
              sections: responseData.sections || {},
            },
          },
        })
      );
    } catch (error) {
      document.dispatchEvent(
        new CustomEvent('toast:open', {
          detail: {
            type: 'error',
            message: error.message || "Impossible d'ajouter l'échantillon.",
          },
        })
      );
    } finally {
      this.button.classList.remove('is-loading');
      this.button.disabled = false;
    }
  }
}

if (!customElements.get('sample-cta')) {
  customElements.define('sample-cta', SampleCta);
}
